import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Container, 
  Card, 
  Form, 
  Button, 
  Pagination, 
  Spinner, 
  Alert
} from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { getRoleDisplayLabel, MERCHANT_LABEL, PROCESSOR_LABEL, SUB_MERCHANT_LABEL } from '../../utils/displayLabels';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { useWindowRefresh } from '../../hooks/useWindowRefresh';
import { compareByNewest, sortByNewest } from '../../utils/sortByNewest';

interface Settlement {
  id: number;
  transactionId: number;
  type: 'Company' | 'Admin' | 'Agent' | 'SuperAgent' | 'Unknown';
  toWallet: string;
  amount: string;
  createtAt: string;
  createtBy: string;
  txHash: string;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface DisplaySettlement extends Settlement {
  displayType: string;
}

interface SettlementGroup {
  transactionId: number;
  settlements: DisplaySettlement[];
  latestCreatedAt: string;
  totalAmount: number;
  settlementCount: number;
  uniqueWalletCount: number;
  uniqueTxHashes: string[];
  breakdownSummary: string;
}

interface SettlementFilters {
  status: string;
  dateFrom: string;
  dateTo: string;
  userId: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const getSettlementSortOrder = (type: Settlement['type']) => {
  switch (type) {
    case 'Company':
      return 0;
    case 'Admin':
      return 1;
    case 'SuperAgent':
      return 2;
    case 'Agent':
      return 3;
    default:
      return 4;
  }
};

const buildDisplaySettlements = (settlements: Settlement[]): DisplaySettlement[] => {
  const grouped = new Map<number, Settlement[]>();

  settlements.forEach((settlement) => {
    const group = grouped.get(settlement.transactionId) ?? [];
    group.push(settlement);
    grouped.set(settlement.transactionId, group);
  });

  const displayMap = new Map<number, DisplaySettlement>();

  grouped.forEach((groupSettlements) => {
    const sorted = [...groupSettlements].sort((left, right) => {
      const orderDelta = getSettlementSortOrder(left.type) - getSettlementSortOrder(right.type);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      return left.id - right.id;
    });

    const visible = sorted.filter((settlement) => settlement.type !== 'Company');
    visible.forEach((settlement, index) => {
      let displayType: string = settlement.type;

      if (visible.length === 1) {
        displayType =
          settlement.type === 'Admin' ? PROCESSOR_LABEL :
          settlement.type === 'SuperAgent' ? MERCHANT_LABEL :
          settlement.type === 'Agent' ? SUB_MERCHANT_LABEL :
          settlement.type;
      } else if (visible.length === 2) {
        displayType = index === 0 ? PROCESSOR_LABEL : MERCHANT_LABEL;
      } else {
        displayType =
          index === 0 ? PROCESSOR_LABEL :
          index === visible.length - 1 ? SUB_MERCHANT_LABEL :
          MERCHANT_LABEL;
      }

      displayMap.set(settlement.id, {
        ...settlement,
        displayType,
      });
    });
  });

  return settlements
    .map((settlement) => displayMap.get(settlement.id))
    .filter((settlement): settlement is DisplaySettlement => settlement !== undefined);
};

const buildSettlementGroups = (settlements: DisplaySettlement[]): SettlementGroup[] => {
  const grouped = new Map<number, DisplaySettlement[]>();
  const transactionOrder: number[] = [];

  settlements.forEach((settlement) => {
    if (!grouped.has(settlement.transactionId)) {
      grouped.set(settlement.transactionId, []);
      transactionOrder.push(settlement.transactionId);
    }

    grouped.get(settlement.transactionId)?.push(settlement);
  });

  return transactionOrder.map((transactionId) => {
    const sortedSettlements = [...(grouped.get(transactionId) ?? [])].sort((left, right) => {
      const orderDelta = getSettlementSortOrder(left.type) - getSettlementSortOrder(right.type);
      if (orderDelta !== 0) {
        return orderDelta;
      }

      return left.id - right.id;
    });

    const latestCreatedAt = sortedSettlements.reduce((latest, settlement) => {
      if (!latest) {
        return settlement.createtAt;
      }

      return new Date(settlement.createtAt).getTime() > new Date(latest).getTime()
        ? settlement.createtAt
        : latest;
    }, '');

    return {
      transactionId,
      settlements: sortedSettlements,
      latestCreatedAt,
      totalAmount: sortedSettlements.reduce((sum, settlement) => {
        const amount = Number.parseFloat(settlement.amount);
        return Number.isFinite(amount) ? sum + amount : sum;
      }, 0),
      settlementCount: sortedSettlements.length,
      uniqueWalletCount: new Set(sortedSettlements.map((settlement) => settlement.toWallet)).size,
      uniqueTxHashes: Array.from(new Set(sortedSettlements.map((settlement) => settlement.txHash).filter(Boolean))),
      breakdownSummary: sortedSettlements.map((settlement) => settlement.displayType).join(' / '),
    };
  }).sort((left, right) => compareByNewest(left, right, {
    dateKeys: ['latestCreatedAt'],
    numericFallbackKeys: ['transactionId'],
  }));
};

const Settlements: React.FC = () => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SettlementFilters>({
    status: '',
    dateFrom: '',
    dateTo: '',
    userId: ''
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const [expandedTransactionIds, setExpandedTransactionIds] = useState<Set<number>>(new Set());
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const displaySettlements = useMemo(() => buildDisplaySettlements(settlements), [settlements]);
  const settlementGroups = useMemo(() => buildSettlementGroups(displaySettlements), [displaySettlements]);

  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();
  const canScopeUsers = userRole === 'Admin' || userRole === 'SuperAgent';

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await dashboardService.getUsers();
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const buildSettlementQueryParams = useCallback((includePagination: boolean = true) => {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.userId) params.append('userId', filters.userId);

    if (includePagination) {
      params.append('page', pagination.currentPage.toString());
      params.append('pageSize', pagination.itemsPerPage.toString());
    }

    return params;
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  const downloadCsv = useCallback(async (path: string, defaultFileName: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(path, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileNameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] || defaultFileName;

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const fetchSettlements = useCallback(async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = buildSettlementQueryParams();

      const url = `${API_ENDPOINTS.TRANSACTIONS}/payouts?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const settlementArray = Array.isArray(data)
        ? data
        : Array.isArray(data.items)
          ? data.items
          : [];

      setSettlements(sortByNewest(settlementArray, {
        dateKeys: ['createtAt'],
        numericFallbackKeys: ['transactionId', 'id'],
      }));

      if (!Array.isArray(data) && typeof data.count === 'number') {
        const effectivePage = typeof data.page === 'number' && data.page > 0 ? data.page : pagination.currentPage;
        const effectivePageSize = typeof data.pageSize === 'number' && data.pageSize > 0 ? data.pageSize : pagination.itemsPerPage;

        setPagination(prev => ({
          ...prev,
          currentPage: effectivePage,
          totalPages: Math.max(1, Math.ceil(data.count / effectivePageSize)),
          totalItems: data.count,
          itemsPerPage: effectivePageSize
        }));
      } else if (settlementArray.length < pagination.itemsPerPage) {
        setPagination(prev => ({
          ...prev,
          totalPages: prev.currentPage,
          totalItems: (prev.currentPage - 1) * prev.itemsPerPage + settlementArray.length
        }));
      } else {
        setPagination(prev => ({
          ...prev,
          totalPages: prev.currentPage + 1,
          totalItems: prev.currentPage * prev.itemsPerPage
        }));
      }
      
    } catch (error) {
      console.error('Error fetching settlements:', error);
      if (!silent) {
        setError(error instanceof Error ? error.message : 'Failed to fetch settlements');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [buildSettlementQueryParams, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchSettlements();
  }, [fetchSettlements]);

  useWindowRefresh(() => {
    void fetchSettlements(true);
  }, {
    intervalMs: 30000,
  });

  useEffect(() => {
    setExpandedTransactionIds((previous) => {
      const visibleTransactionIds = new Set(settlementGroups.map((group) => group.transactionId));
      const next = new Set<number>();

      previous.forEach((transactionId) => {
        if (visibleTransactionIds.has(transactionId)) {
          next.add(transactionId);
        }
      });

      return next;
    });
  }, [settlementGroups]);

  const handleFilterChange = (key: keyof SettlementFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handleUserSelect = (selectedUserId: string) => {
    setFilters(prev => ({
      ...prev,
      userId: selectedUserId
    }));
    setSearchTerm('');
    setShowUserDropdown(false);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleRefresh = () => {
    fetchSettlements();
  };

  const handleExportCsv = async () => {
    try {
      setError(null);
      const params = buildSettlementQueryParams(false);
      const queryString = params.toString();
      const endpoint = `${API_ENDPOINTS.TRANSACTIONS}/payouts/export/csv${queryString ? `?${queryString}` : ''}`;
      await downloadCsv(endpoint, 'payouts-export.csv');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export settlements');
    }
  };

  const getStatusBadge = (status: string) => {
    const tone =
      status === 'Completed' ? 'success' :
      status === 'Pending' ? 'warning' :
      status === 'Failed' ? 'danger' :
      'neutral';

    return (
      <span className={`app-status-pill app-status-pill--${tone}`}>
        {status}
      </span>
    );
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: string | number): string => {
    const numericAmount = typeof amount === 'number'
      ? amount
      : Number.parseFloat(amount);

    if (!Number.isFinite(numericAmount)) {
      return String(amount);
    }

    return numericAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  const truncateAddress = (address: string): string => {
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getGroupStatusValue = (group: SettlementGroup): 'Pending' | 'Completed' | 'Failed' | 'Mixed' => {
    const uniqueStatuses = Array.from(new Set(group.settlements.map((settlement) => settlement.status)));

    if (uniqueStatuses.length === 1) {
      return uniqueStatuses[0];
    }

    return 'Mixed';
  };

  const toggleTransactionGroup = (transactionId: number) => {
    setExpandedTransactionIds((previous) => {
      const next = new Set(previous);
      if (next.has(transactionId)) {
        next.delete(transactionId);
      } else {
        next.add(transactionId);
      }

      return next;
    });
  };

  const getGroupStatusBadge = (group: SettlementGroup) => {
    const status = getGroupStatusValue(group);

    if (status === 'Mixed') {
      return (
        <span className="app-status-pill app-status-pill--mixed">
          Mixed
        </span>
      );
    }

    return getStatusBadge(status);
  };

  const renderGroupTxLink = (group: SettlementGroup) => {
    if (group.uniqueTxHashes.length === 0) {
      return <span className="operator-muted-text">Pending</span>;
    }

    if (group.uniqueTxHashes.length === 1) {
      return (
        <a
          href={`https://polygonscan.com/tx/${group.uniqueTxHashes[0]}`}
          target="_blank"
          rel="noopener noreferrer"
          className="app-table-link"
          onClick={(event) => event.stopPropagation()}
        >
          View on PolygonScan
        </a>
      );
    }

    return <span className="operator-muted-text">{group.uniqueTxHashes.length} payout txs</span>;
  };

  const filteredUsers = useMemo((): User[] => {
    let filteredUsers: User[] = [];
    
    if (userRole === 'Admin') {
      filteredUsers = users;
    } else if (userRole === 'SuperAgent') {
      filteredUsers = users.filter(user => 
        user.id === parseInt(userId || '0') || 
        (user.role === 'Agent' && user.id !== parseInt(userId || '0'))
      );
    } else {
      filteredUsers = users.filter(user => user.id === parseInt(userId || '0'));
    }

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return filteredUsers
      .filter((user) => {
        if (!normalizedSearchTerm) {
          return true;
        }

        const haystack = [
          user.name,
          user.email,
          user.settlementWallet,
          getRoleDisplayLabel(user.role),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(normalizedSearchTerm);
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [searchTerm, userId, userRole, users]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedUser = useMemo(
    () => users.find((user) => user.id.toString() === filters.userId) ?? null,
    [filters.userId, users]
  );

  const settlementSummary = useMemo(() => {
    return settlementGroups.reduce((summary, group) => {
      const groupStatus = getGroupStatusValue(group);

      summary.totalAmount += group.totalAmount;
      summary.totalPayoutLines += group.settlementCount;

      if (groupStatus === 'Completed') {
        summary.completedGroups += 1;
      } else if (groupStatus === 'Pending') {
        summary.pendingGroups += 1;
      } else if (groupStatus === 'Failed') {
        summary.failedGroups += 1;
      } else {
        summary.mixedGroups += 1;
      }

      return summary;
    }, {
      totalAmount: 0,
      totalPayoutLines: 0,
      completedGroups: 0,
      pendingGroups: 0,
      failedGroups: 0,
      mixedGroups: 0,
    });
  }, [settlementGroups]);

  const activeFilterCount = useMemo(() => (
    [
      filters.status,
      filters.dateFrom,
      filters.dateTo,
      filters.userId,
    ].filter(Boolean).length
  ), [filters.dateFrom, filters.dateTo, filters.status, filters.userId]);

  const needsAttentionCount = settlementSummary.pendingGroups + settlementSummary.failedGroups + settlementSummary.mixedGroups;

  const summaryCardStyle = (accent: string) => (
    { ['--summary-accent' as '--summary-accent']: accent } as React.CSSProperties
  );

  const clearFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      userId: '',
    });
    setSearchTerm('');
    setShowUserDropdown(false);
    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="app-pagination app-pagination--operator justify-content-center flex-wrap mt-4">
        <Pagination.Prev 
          disabled={pagination.currentPage === 1}
          onClick={() => pagination.currentPage > 1 && handlePageChange(pagination.currentPage - 1)}
        />
        {items}
        <Pagination.Next 
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => pagination.currentPage < pagination.totalPages && handlePageChange(pagination.currentPage + 1)}
        />
      </Pagination>
    );
  };

  if (loading && settlements.length === 0) {
    return (
      <div className="operator-page settlements-page">
        <Container fluid className="operator-page__container">
          <Card className="operator-card settlements-page__loading-card">
            <Card.Body className="text-center py-5">
              <Spinner animation="border" role="status" className="app-spinner">
                <span className="visually-hidden">Loading settlements...</span>
              </Spinner>
              <p className="operator-muted-text mb-0 mt-3">
                Loading the latest payout groups...
              </p>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="operator-page settlements-page">
      <Container fluid className="operator-page__container">
        <InternalPageHeader
          eyebrow="Payout Tracking"
          title="Settlements"
          description="Track payout groups, spot stalled payouts, and open the chain record when you need it."
          actions={(
            <div className="app-toolbar-group">
              <Button
                variant="outline-light"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Refresh'}
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleExportCsv()}
              >
                Export CSV
              </Button>
            </div>
          )}
          meta={[
            { label: 'Groups in view', value: settlementGroups.length.toString() },
            { label: 'Settlement in view', value: formatAmount(settlementSummary.totalAmount) },
            { label: 'Scope', value: selectedUser ? selectedUser.name : getRoleDisplayLabel(userRole) },
            { label: 'Needs follow-up', value: needsAttentionCount.toString() },
          ]}
        />

        <Card className='operator-card mt-4 settlements-page__filters-card'>
          <Card.Header className='operator-card__header'>
            <div>
              <h5 className='operator-card__title'>View controls</h5>
              <div className='operator-card__subtitle'>
                Narrow payout groups by status, date, or account.
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className='settlements-page__filters-layout'>
              <div className='settlements-page__quick-filters'>
                {[
                  { label: 'All groups', value: '' },
                  { label: 'Waiting', value: 'Pending' },
                  { label: 'Completed', value: 'Completed' },
                  { label: 'Needs attention', value: 'Failed' },
                ].map((option) => {
                  const isActive = filters.status === option.value;

                  return (
                    <Button
                      key={option.label}
                      variant={isActive ? 'primary' : 'outline-light'}
                      className='settlements-page__quick-filter'
                      onClick={() => handleFilterChange('status', option.value)}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>

              <div className='settlements-page__filter-grid'>
                <Form.Group>
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    className='operator-control'
                    type='datetime-local'
                    value={filters.dateFrom}
                    onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Until</Form.Label>
                  <Form.Control
                    className='operator-control'
                    type='datetime-local'
                    value={filters.dateTo}
                    onChange={(event) => handleFilterChange('dateTo', event.target.value)}
                  />
                </Form.Group>

                {canScopeUsers && (
                  <Form.Group>
                    <Form.Label>Account</Form.Label>
                    <div className={`operator-search-surface ${showUserDropdown ? 'operator-search-surface--open' : ''}`} ref={userDropdownRef}>
                      <Form.Control
                        className='operator-control'
                        type='text'
                        placeholder='Search merchant or sub merchant'
                        value={searchTerm}
                        onChange={(event) => {
                          setSearchTerm(event.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                      />
                      <button
                        type='button'
                        className='operator-search-toggle'
                        onClick={() => setShowUserDropdown((previous) => !previous)}
                        aria-label='Toggle account filter'
                      >
                        <span className='operator-toggle-indicator'>
                          {showUserDropdown ? '-' : '+'}
                        </span>
                      </button>

                      {showUserDropdown && (
                        <div className='operator-search-menu'>
                          <button
                            type='button'
                            className={`operator-search-option ${filters.userId === '' ? 'operator-search-option--active' : ''}`}
                            onClick={() => handleUserSelect('')}
                          >
                            All accounts
                            <span className='operator-search-option__meta'>
                              Show every payout group available to your current role.
                            </span>
                          </button>

                          {filteredUsers.length === 0 ? (
                            <div className='settlements-page__search-empty'>
                              No accounts match this search yet.
                            </div>
                          ) : (
                            filteredUsers.map((user) => (
                              <button
                                type='button'
                                key={user.id}
                                className={`operator-search-option ${filters.userId === user.id.toString() ? 'operator-search-option--active' : ''}`}
                                onClick={() => handleUserSelect(user.id.toString())}
                              >
                                {user.name}
                                <span className='operator-search-option__meta'>
                                  {getRoleDisplayLabel(user.role)} | {user.email}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                )}

                <div className='settlements-page__filter-help'>
                  <span className='settlements-page__filter-help-title'>Current view</span>
                  <span className='settlements-page__filter-help-copy'>
                    {selectedUser
                      ? `Scoped to ${selectedUser.name}.`
                      : `Showing every payout group visible to ${getRoleDisplayLabel(userRole)}.`}{' '}
                    Refresh or export from the header when you need the latest copy.
                  </span>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className='settlements-page__active-filters'>
                  <span className='settlements-page__active-filters-label'>
                    {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
                  </span>
                  <Button
                    variant='outline-light'
                    size='sm'
                    onClick={clearFilters}
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant='danger' className='mb-4 mt-4'>
            {error}
          </Alert>
        )}

        <div className='operator-summary-grid mt-4'>
          <div className='operator-summary-card' style={summaryCardStyle('var(--app-blue)')}>
            <div className='operator-summary-card__label'>Payout Groups</div>
            <div className='operator-summary-card__value'>{settlementGroups.length}</div>
            <div className='operator-summary-card__note'>
              Transaction groups with one or more payout lines in the current view.
            </div>
          </div>
          <div className='operator-summary-card' style={summaryCardStyle('var(--app-gray-300)')}>
            <div className='operator-summary-card__label'>Completed</div>
            <div className='operator-summary-card__value'>{settlementSummary.completedGroups}</div>
            <div className='operator-summary-card__note'>
              Groups that already finished every payout in this view.
            </div>
          </div>
          <div className='operator-summary-card' style={summaryCardStyle('var(--app-gray-400)')}>
            <div className='operator-summary-card__label'>Needs Follow-Up</div>
            <div className='operator-summary-card__value'>{needsAttentionCount}</div>
            <div className='operator-summary-card__note'>
              Waiting, failed, or mixed groups that may need another look.
            </div>
          </div>
          <div className='operator-summary-card' style={summaryCardStyle('var(--app-blue)')}>
            <div className='operator-summary-card__label'>Settlement In View</div>
            <div className='operator-summary-card__value'>{formatAmount(settlementSummary.totalAmount)}</div>
            <div className='operator-summary-card__note'>
              Combined payout value across {settlementSummary.totalPayoutLines} visible payout lines.
            </div>
          </div>
        </div>

        <Card className='operator-card mt-4 settlements-page__results-card'>
          <Card.Header className='operator-card__header d-flex justify-content-between align-items-start gap-3 flex-wrap'>
            <div>
              <h5 className='operator-card__title'>Payout groups</h5>
              <div className='operator-card__subtitle'>
                Open a group to see who received funds, which wallet was used, and whether chain confirmation is already available.
              </div>
            </div>
            <span className='operator-muted-text'>
              {settlementGroups.length} group{settlementGroups.length === 1 ? '' : 's'} in view
            </span>
          </Card.Header>
          <Card.Body>
            {loading && settlements.length > 0 && (
              <div className='settlements-page__refresh-banner'>
                Refreshing the current payout view...
              </div>
            )}

            {settlementGroups.length === 0 && !loading ? (
              <div className='app-empty-state settlements-page__empty-state'>
                <p className='mb-2'>No payout groups match this view yet.</p>
                <p className='mb-0'>
                  Adjust the status, date range, or account scope to widen the results.
                </p>
              </div>
            ) : (
              <div className='settlements-page__group-list'>
                {settlementGroups.map((group) => {
                  const isExpanded = expandedTransactionIds.has(group.transactionId);
                  const groupStatus = getGroupStatusValue(group);

                  return (
                    <article
                      key={group.transactionId}
                      className={`settlements-page__group ${isExpanded ? 'settlements-page__group--expanded' : ''}`}
                    >
                      <div className='settlements-page__group-shell'>
                        <div className='settlements-page__group-main'>
                          <div className='settlements-page__group-eyebrow'>
                            Transaction #{group.transactionId}
                          </div>
                          <h6 className='settlements-page__group-title'>
                            {group.breakdownSummary}
                          </h6>
                          <div className='settlements-page__group-meta'>
                            Updated {formatDateTime(group.latestCreatedAt)}
                          </div>
                        </div>

                        <div className='settlements-page__group-side'>
                          <div className='settlements-page__group-total-label'>Total paid out</div>
                          <div className='settlements-page__group-total-value'>
                            {formatAmount(group.totalAmount)}
                          </div>
                          <div className='settlements-page__group-status'>
                            {getGroupStatusBadge(group)}
                          </div>
                        </div>
                      </div>

                      <div className='settlements-page__group-stats'>
                        <div className='settlements-page__group-stat'>
                          <span className='settlements-page__group-stat-label'>Payout lines</span>
                          <strong>{group.settlementCount}</strong>
                        </div>
                        <div className='settlements-page__group-stat'>
                          <span className='settlements-page__group-stat-label'>
                            {userRole === 'Admin' ? 'Recipient wallets' : 'Payout destinations'}
                          </span>
                          <strong>{group.uniqueWalletCount}</strong>
                        </div>
                        <div className='settlements-page__group-stat'>
                          <span className='settlements-page__group-stat-label'>Chain records</span>
                          <strong>
                            {group.uniqueTxHashes.length === 0
                              ? 'Pending'
                              : group.uniqueTxHashes.length === 1
                                ? '1 tx'
                                : `${group.uniqueTxHashes.length} txs`}
                          </strong>
                        </div>
                        <div className='settlements-page__group-actions'>
                          <div className='settlements-page__group-link'>
                            {renderGroupTxLink(group)}
                          </div>
                          <Button
                            variant='outline-light'
                            className='settlements-page__details-button'
                            onClick={() => toggleTransactionGroup(group.transactionId)}
                          >
                            {isExpanded ? 'Hide payout details' : 'Show payout details'}
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className='settlements-page__detail-shell'>
                          <div className='settlements-page__detail-intro'>
                            <div>
                              <div className='settlements-page__detail-eyebrow'>
                                Detailed payout lines
                              </div>
                              <div className='settlements-page__detail-copy'>
                                Review each recipient and open the matching chain record when it is available.
                              </div>
                            </div>
                            <div className='settlements-page__detail-status-note'>
                              Group status: {groupStatus}
                            </div>
                          </div>

                          <div className='settlements-page__detail-list'>
                            {group.settlements.map((settlement) => (
                              <article key={settlement.id} className='settlements-page__detail-item'>
                                <div className='settlements-page__detail-top'>
                                  <div>
                                    <div className='settlements-page__detail-role'>
                                      {settlement.displayType}
                                    </div>
                                    <div className='settlements-page__detail-time'>
                                      {formatDateTime(settlement.createtAt)}
                                    </div>
                                  </div>
                                  <div className='settlements-page__detail-amount'>
                                    {formatAmount(settlement.amount)}
                                  </div>
                                </div>

                                <div className='settlements-page__detail-meta'>
                                  <div className='settlements-page__detail-status'>
                                    {getStatusBadge(settlement.status)}
                                  </div>

                                  {userRole === 'Admin' && (
                                    <div className='settlements-page__detail-wallet' title={settlement.toWallet}>
                                      <span className='settlements-page__detail-meta-label'>Wallet</span>
                                      <strong>{truncateAddress(settlement.toWallet)}</strong>
                                    </div>
                                  )}

                                  <div className='settlements-page__detail-link'>
                                    {settlement.txHash ? (
                                      <a
                                        href={`https://polygonscan.com/tx/${settlement.txHash}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='app-table-link'
                                      >
                                        Open chain tx
                                      </a>
                                    ) : (
                                      <span className='operator-muted-text'>Chain record pending</span>
                                    )}
                                  </div>
                                </div>
                              </article>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        {renderPagination()}
      </Container>
    </div>
  );
};

export default Settlements;




